import React, { useState, useRef } from 'react';
import styled from '@emotion/styled';
import Select from 'react-select';
import { DateRangePicker } from 'react-dates';
import { ReactComponent as DeleteIcon } from 'Icons/delete.svg';
import { ReactComponent as EditIcon } from 'Icons/editIconT1.svg';
import { ReactComponent as CancelIcon } from 'Icons/CloseCircle.svg';
import Autosuggest from './AutoSuggest';
import { debounce } from 'Utils/storage';
import Moment from 'moment';

const TableWrapper = styled.div`
  width: 100%;
  overflow-x: auto;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  background: white;
  -ms-overflow-style: none;
  scrollbar-width: none;
  min-height: calc(100vh - 280px);
  
  &::-webkit-scrollbar {
    display: none;
  }
`;

const Table = styled.table`
  width: 100%;
  min-width: 2300px;
  border-collapse: collapse;
  table-layout: auto;
`;

const Th = styled.th`
  background: #f8f9ff;
  padding: 10px 5px 10px 10px;
  text-align: center;
  font-weight: 600;
  color: #1a1a1a;
  border-bottom: 1px solid #e6e6e6;
  // position: sticky;
  // top: 0;
  // z-index: 1;
  width: ${props => props.width || 'auto'};
`;

const Td = styled.td`
  padding: 10px 5px 10px 10px;
  border-bottom: 1px solid #e6e6e6;
  color: #4b5563;
  // overflow: hidden;
  text-align: center;
  width: ${props => props.width || 'auto'};
`;

const Tr = styled.tr`
  &:hover {
    background: #f8f9ff;
  }
`;

const Input = styled.input`
  width: 100%;
  padding: 8px 12px;
  border: 1px solid ${props => props.hasError ? '#FF3B30' : '#e5e7eb'};
  border-radius: 4px;
  font-size: 14px;
  transition: border-color 0.2s;
  height: 38px;
  background: 'white';

  &:focus {
    outline: none;
    border-color: ${props => props.hasError ? '#FF3B30' : '#597EF7'};
  }
`;

const StyledSelect = styled(Select)`
  width: 100%;
  position: relative;
  
  .select__control {
    border: 1px solid ${props => props.hasError ? '#FF3B30' : '#E0E0E0'};
    border-radius: 4px;
    min-height: 40px;
    background: ${props => props.hasError ? '#FFF0F0' : 'white'};
    box-shadow: none;
    &:hover {
      border-color: #E0E0E0;
    }
  }

  .select__menu {
    z-index: 9999;
    background: white;
    border: 1px solid #E0E0E0;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }

  .select__option {
    padding: 8px 12px;
    cursor: pointer;
    &:hover {
      background: #F5F5F5;
    }
  }

  .select__option--is-focused {
    background: #F5F5F5;
  }

  .select__option--is-selected {
    background: #E3F2FD;
    color: #1976D2;
  }
`;

const ErrorWrapper = styled.div`
  border: 1px solid #FF3B30;
  border-radius: 4px;
  background: #FFF0F0;
`;

const DateRangeWrapper = styled.div`
  position: relative;
  width: 100%;
  
  .DateRangePicker {
    width: 100%;
  }

  .DateRangePickerInput {
    width: 100%;
    height: 35px;
    display: flex;
    align-items: center;
    border: 1px solid #e5e7eb;
    border-radius: 4px;
    background: white;
  }

  .DateInput {
    width: 120px;
    height: 100%;
    padding: 0 5px;
  }

  .DateInput_input {
    height: 100%;
    border: none;
    background: transparent;
    text-align: center;
    &:focus {
      outline: none;
    }
  }

  .DateRangePickerInput_arrow {
    margin: 0 8px;
  }

  .DateRangePickerInput_clearDates {
    margin: 0 8px;
    padding: 0;
    background: none;
    border: none;
    cursor: pointer;
    &:hover {
      background: none;
    }
  }

  .CalendarDay {
    border: none;
    &:hover {
      background: #f8f9ff;
    }
  }

  .CalendarDay__selected {
    background: #597EF7;
    color: white;
    &:hover {
      background: #597EF7;
    }
  }

  .CalendarDay__hovered_span {
    background: #f8f9ff;
    color: #1a1a1a;
    &:hover {
      background: #f8f9ff;
    }
  }

  .CalendarDay__selected_span {
    background: #f8f9ff;
    color: #1a1a1a;
    &:hover {
      background: #f8f9ff;
    }
  }

  .DayPicker {
    z-index: 9999;
  }

  .DayPicker__withBorder {
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    border: 1px solid #e5e7eb;
    border-radius: 4px;
  }
  .DateRangePickerInput.DateRangePickerInput_1::after {
    left: 48%;
    top: 10px;
  }
`;

const ActionButton = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s;
  color: #597EF7;
  margin-right: 8px;

  &:hover {
    background: #f8f9ff;
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

const SaveButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 4px 12px;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  border: 1px solid #597EF7;
  color: #597EF7;
  height: 28px;

  &:hover {
    background: #1d4ed8;
    color: #fff;
  }
`;

const DataTable = ({
  data,
  isAdding,
  newRecord,
  onNewRecordChange,
  onDateChange,
  onSaveNewRecord,
  onCancelAdd,
  onEdit,
  onDelete,
  transportersList,
  vehicleTypes,
  vehicleNumberDetails,
  getVehicleNumberDetails,
  routeCodesDetails,
  pricingBasis,
  getTTBilRouteCodeDetails,
  poDropdownLists,
  currentPage,
  missingFields = [],
}) => {

  const [editingId, setEditingId] = useState(null);
  const [editRecord, setEditRecord] = useState(null);
  const [focusedInput, setFocusedInput] = useState(null);
  const { contractType  } = poDropdownLists;
  
  const handleEdit = (record) => {
    setEditingId(record.transport_management_id);
    setEditRecord(record);
  };

  const handleEditChange = (field, value) => {
    setEditRecord(prev => ({
      ...prev,
      [['provider', 'billing_parameter', 'contract_type'].includes(field) ? `${field}_name` : field]: typeof value === 'object' && value !== null ? (value.name || (['route_code', 'excluded_route_code', 'included_route_code'].includes(field) ? value['sap_code'] : value[field]) || value.value) : ((['fixed_cost', 'trip_count', 'freight_rate'].includes(field) && value) ? +value : value),
      ...(typeof value === 'object' && value !== null && {
        [`${field}_id`]: value.id || value.value
      })
    }));
    if(field === 'vehicle_reg_no' && value && typeof value === 'string') {
      getVehicleNumberDetails(value);
    } else if(['route_code', 'excluded_route_code', 'included_route_code'].includes(field) && typeof value === 'string') {
      getTTBilRouteCodeDetails(value);
    }
  };

  const handleSaveEdit = () => {
    onEdit(editRecord);
    setEditingId(null);
    setEditRecord(null);
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditRecord(null);
  };

  const handleEditDateChange = ({ startDate, endDate }) => {
    setEditRecord(prev => ({
      ...prev,
      contract_tenure_from: startDate && Moment(startDate).valueOf(),
      contract_tenure_to: endDate && Moment(endDate).valueOf()
    }));
  };

  const renderCell = (record, field) => {
    if (editingId === record.transport_management_id) {
      switch (field) {
        case 'provider_name':
          return (
            <StyledSelect
              value={editRecord.provider_id ? transportersList.find(t => t.id === editRecord.provider_id) : null}
              onChange={(option) => handleEditChange('provider', option)}
              options={transportersList || []}
              getOptionLabel={(option) => option.name}
              getOptionValue={(option) => option.id}
              placeholder="Select Transporter"
              // menuPortalTarget={document.body}
              hasError={missingFields.includes('provider_name')}
            />
          );
        case 'vehicle_type':
          return (
            <StyledSelect
              value={editRecord.vehicle_type ? vehicleTypes.find(v => v.name === editRecord.vehicle_type) : null}
              onChange={(option) => handleEditChange('vehicle_type', option)}
              options={vehicleTypes || []}
              getOptionLabel={(option) => option.name}
              getOptionValue={(option) => option.value}
              placeholder="Select Vehicle Type"
              // menuPortalTarget={document.body}
              styles={{
                control: (base, state) => ({
                  ...base,
                  borderColor: missingFields.includes('provider_name') ? '#FF3B30' : base.borderColor,
                  background: missingFields.includes('provider_name') ? '#FFF0F0' : base.background,
                  boxShadow: 'none',
                  '&:hover': {
                    borderColor: missingFields.includes('provider_name') ? '#FF3B30' : base.borderColor,
                  }
                })
              }}
              hasError={missingFields.includes('vehicle_type')}
            />
          );
        case 'contract_type_name':
          return (
            <StyledSelect
              value={editRecord.contract_type_name ? contractType.find(v => v.value === editRecord.contract_type_name) : null}
              onChange={(option) => handleEditChange('contract_type', option)}
              options={contractType || []}
              getOptionLabel={(option) => option.value}
              getOptionValue={(option) => option.id}
              placeholder="Select Contract type"
              // menuPortalTarget={document.body}
              hasError={missingFields.includes('contract_type_name')}
            />
          );
        case 'billing_parameter_name':
          return (
            <StyledSelect
              value={editRecord['billing_parameter_name'] && pricingBasis.find(el => el.name === editRecord['billing_parameter_name'])}
              onChange={(option) => handleEditChange('billing_parameter', option)}
              options={pricingBasis || []}
              getOptionLabel={(option) => option.name}
              getOptionValue={(option) => option.name}
              placeholder="Select billing parameter"
              // menuPortalTarget={document.body}
              hasError={missingFields.includes('billing_parameter_name')}
              styles={{
                menuPortal: (base) => ({
                  ...base,
                  zIndex: 9999
                })
              }}
            />
          );
        case 'vehicle_reg_no':
          return (
            <Autosuggest 
              name={field}
              inputValue={editRecord[field] || ''}
              options={vehicleNumberDetails || []}
              type="vehicle_reg_no"
              onChange={(e) => handleEditChange(field, e.target.value)}
              placeholder={`Enter Vehicle No.`}
              onSelect={(option) => handleEditChange(field, option)}
              displayName="vehicle_reg_no"
              singleMode={true}
            />
          );
        case 'route_code':
          return (
            <Autosuggest
              name={field}
              inputValue={editRecord[field] || ''}
              options={routeCodesDetails || []}
              type="route_code"
              onChange={(e) => handleEditChange(field, e.target.value)}
              placeholder={`Enter Route Code`}
              onSelect={(option) => handleEditChange(field, option)}
              displayName="sap_code"
              singleMode={true}
              readOnly={editingId}
            />
          );
        case 'contract':
          return (
           <DateRangeWrapper>
            <DateRangePicker
              startDate={editRecord.contract_tenure_from && Moment(editRecord.contract_tenure_from)}
              endDate={editRecord.contract_tenure_to && Moment(editRecord.contract_tenure_to)}
              onDatesChange={handleEditDateChange}
              focusedInput={focusedInput}
              onFocusChange={focusedInput => setFocusedInput(focusedInput)}
              startDatePlaceholderText="Start"
              endDatePlaceholderText="End"
              displayFormat="DD/MM/YYYY"
              numberOfMonths={2}
              showClearDates
              small
              withPortal
              appendToBody
              isOutsideRange={() => false}
            />
           </DateRangeWrapper>  
          );
        case 'actions':
          return (
            <div style={{ display: 'flex', justifyContent: 'center' , alignItems: 'center', gap: '8px'}}>
              <SaveButton onClick={handleSaveEdit}>Update</SaveButton>
              <CancelIcon fill={'#FF3B30'} onClick={handleCancelEdit} width={20} height={20} style={{ cursor: 'pointer' }} />
            </div>
          );
        case 'excluded_route_code':
          return (
            <Autosuggest
              name={field}
              inputValue={editRecord[field] || ''}
              options={routeCodesDetails || []}
              type="excluded_route_code"  
              onChange={(e) => handleEditChange(field, e.target.value)}
              placeholder={`Enter excluded route code`}
              onSelect={(option) => handleEditChange(field, option)}
              displayName="sap_code"
              singleMode={true}
            />
          );
        case 'included_route_code':
          return (
            <Autosuggest
              name={field}
              inputValue={editRecord[field] || ''}    
              options={routeCodesDetails || []}
              type="included_route_code"
              onChange={(e) => handleEditChange(field, e.target.value)}
              placeholder={`Enter included route code`}
              onSelect={(option) => handleEditChange(field, option)}
              displayName="sap_code"  
              singleMode={true}
            />
          );
        default:
          return (
            <Input
              type="text"
              value={editRecord[field] || ''}
              onChange={(e) => handleEditChange(field, e.target.value)}
              placeholder={`Enter ${field}`}
            />
          );
      }
    }
    if(field === 'contract') {
      return `${Moment(record.contract_tenure_from).format('DD/MM/YYYY')} - ${Moment(record.contract_tenure_to).format('DD/MM/YYYY')}`;
    }
    return record[field];
  };

  return (
    <TableWrapper>
      <Table>
        <thead>
          <tr>
            <Th width="80px">Sl No.</Th>
            <Th width="250px">Transporter</Th>
            <Th width="200px">Vehicle Type</Th>
            <Th width="180px">Contract Type</Th>
            <Th width="150px">Route Code</Th>
            <Th width="150px">Vehicle Number</Th>
            <Th width="150px">Fixed Cost</Th>
            <Th width="150px">Excluded Route Code</Th>
            <Th width="150px">Included Route Code</Th>
            <Th width="250px">Billing Parameter</Th>
            <Th width="100px">Trip Count</Th>
            <Th width="100px">Freight Rate</Th>
            <Th width="250px">Contract Tenure</Th>
            <Th>Actions</Th>
          </tr>
        </thead>
        <tbody>
          {isAdding && (
            <tr>
              <Td>{''}</Td>
              <Td>
                <StyledSelect
                  value={newRecord.provider_name ? transportersList.find(transporter => transporter.provider_name === newRecord.provider_name) : null}
                  onChange={(option) => onNewRecordChange('provider', option)}
                  options={transportersList || []}
                  getOptionLabel={(option) => option.name}
                  getOptionValue={(option) => option.id}
                  placeholder="Select transporter"
                  // menuPortalTarget={document.body}
                  styles={{
                    control: (base, state) => ({
                      ...base,
                      borderColor: missingFields.includes('provider_name') && !newRecord.provider_name ? '#FF3B30' : base.borderColor,
                      '&:hover': {
                        borderColor: missingFields.includes('provider_name') && !newRecord.provider_name ? '#FF3B30' : base.borderColor,
                      }
                    })
                  }}
                />
              </Td>
              <Td>
                <StyledSelect
                  value={newRecord.vehicle_type ? vehicleTypes.find(v => v.name === newRecord.vehicle_type) : null}
                  onChange={(option) => onNewRecordChange('vehicle_type', option)}
                  options={vehicleTypes || []}
                  getOptionLabel={(option) => option.name}
                  getOptionValue={(option) => option.value}
                  placeholder="Select vehicle type"
                  // menuPortalTarget={document.body}
                  styles={{
                    control: (base, state) => ({
                      ...base,
                      borderColor: missingFields.includes('vehicle_type') && !newRecord.vehicle_type ? '#FF3B30' : base.borderColor,
                      '&:hover': {
                        borderColor: missingFields.includes('vehicle_type') && !newRecord.vehicle_type ? '#FF3B30' : base.borderColor,
                      }
                    })
                  }}
                />
              </Td>
              <Td>
                <StyledSelect
                  value={newRecord.contract_type_name ? contractType.find(v => v.value === newRecord.contract_type_name) : null}
                  onChange={(option) => onNewRecordChange('contract_type', option)}
                  options={contractType || []}
                  getOptionLabel={(option) => option.value}
                  getOptionValue={(option) => option.id}
                  placeholder="Select Contract type"
                  // menuPortalTarget={document.body}
                  styles={{
                    control: (base, state) => ({
                      ...base,
                      borderColor: missingFields.includes('contract_type_name') && !newRecord.contract_type_name ? '#FF3B30' : base.borderColor,
                      '&:hover': {
                        borderColor: missingFields.includes('contract_type_name') && !newRecord.contract_type_name ? '#FF3B30' : base.borderColor,
                      }
                    })
                  }}
                />
              </Td>
              <Td>
                {missingFields.includes('route_code') ? (
                  <ErrorWrapper>
                    <Autosuggest
                      name="route_code"
                      inputValue={newRecord.route_code || ''}
                      options={routeCodesDetails || []}
                      type="route_code"
                      onChange={(e) => onNewRecordChange('route_code', e.target.value)}
                      placeholder={`Enter route codes`}
                      onSelect={(option) => onNewRecordChange('route_code', option)}
                      displayName="sap_code"
                    />
                  </ErrorWrapper>
                ) : (
                  <Autosuggest
                    name="route_code"
                    inputValue={newRecord.route_code || ''}
                    options={routeCodesDetails || []}
                    type="route_code"
                    onChange={(e) => onNewRecordChange('route_code', e.target.value)}
                    placeholder={`Enter route codes`}
                    onSelect={(option) => onNewRecordChange('route_code', option)}
                    displayName="sap_code"
                  />
                )}
              </Td>
              <Td>
                {missingFields.includes('vehicle_reg_no') ? (
                  <ErrorWrapper>
                    <Autosuggest
                      name="vehicle_reg_no"
                      inputValue={newRecord.vehicle_reg_no || ''}
                      options={vehicleNumberDetails || []}
                      type="vehicle_reg_no"
                      onChange={(e) => onNewRecordChange('vehicle_reg_no', e.target.value)}
                      placeholder={`Enter vehicle number`}
                      onSelect={(option) => onNewRecordChange('vehicle_reg_no', option)}
                      displayName="vehicle_reg_no"
                    />
                  </ErrorWrapper>
                ) : (
                  <Autosuggest
                    name="vehicle_reg_no"
                    inputValue={newRecord.vehicle_reg_no || ''}
                    options={vehicleNumberDetails || []}
                    type="vehicle_reg_no"
                    onChange={(e) => onNewRecordChange('vehicle_reg_no', e.target.value)}
                    placeholder={`Enter vehicle number`}
                    onSelect={(option) => onNewRecordChange('vehicle_reg_no', option)}
                    displayName="vehicle_reg_no"
                  />
                )}
              </Td>
              <Td>
                <Input
                  type="number"
                  value={newRecord.fixed_cost}
                  onChange={(e) => onNewRecordChange('fixed_cost', e.target.value)}
                  placeholder="Enter fixed cost"
                  hasError={missingFields.includes('fixed_cost') && !newRecord.fixed_cost}
                />
              </Td>
              <Td>
                {missingFields.includes('excluded_route_code') ? (
                  <ErrorWrapper>
                    <Autosuggest
                      name="excluded_route_code"
                      inputValue={newRecord.excluded_route_code || ''}
                      options={routeCodesDetails || []}
                      type="excluded_route_code"
                      onChange={(e) => onNewRecordChange('excluded_route_code', e.target.value)}
                      placeholder={`Enter excluded route code`}
                      onSelect={(option) => onNewRecordChange('excluded_route_code', option)}
                      displayName="sap_code"
                    />
                  </ErrorWrapper>
                ) : (
                  <Autosuggest
                    name="excluded_route_code"
                    inputValue={newRecord.excluded_route_code || ''}
                    options={routeCodesDetails || []}
                    type="excluded_route_code"
                    onChange={(e) => onNewRecordChange('excluded_route_code', e.target.value)}
                    placeholder={`Enter excluded route code`}
                    onSelect={(option) => onNewRecordChange('excluded_route_code', option)}
                    displayName="sap_code"
                  />
                )}
              </Td>
              <Td>
                {missingFields.includes('included_route_code') ? (
                  <ErrorWrapper>
                    <Autosuggest
                      name="included_route_code"
                      inputValue={newRecord.included_route_code || ''}
                      options={routeCodesDetails || []}
                      type="included_route_code"
                      onChange={(e) => onNewRecordChange('included_route_code', e.target.value)}
                      placeholder={`Enter Included Route Code`}
                      onSelect={(option) => onNewRecordChange('included_route_code', option)}
                      displayName="sap_code"
                    />
                  </ErrorWrapper>
                ) : (
                  <Autosuggest
                    name="included_route_code"
                    inputValue={newRecord.included_route_code || ''}
                    options={routeCodesDetails || []}
                    type="included_route_code"
                    onChange={(e) => onNewRecordChange('included_route_code', e.target.value)}
                    placeholder={`Enter Included Route Code`}
                    onSelect={(option) => onNewRecordChange('included_route_code', option)}
                    displayName="sap_code"
                  />
                )}
              </Td>
              <Td>
                <StyledSelect
                  value={newRecord.billing_parameter_name && pricingBasis.find(el => el.name === newRecord.billing_parameter_name)}
                  onChange={(option) => onNewRecordChange('billing_parameter', option)}
                  options={pricingBasis || []}
                  placeholder="Select billing parameter"
                  // menuPortalTarget={document.body}
                  getOptionLabel={option => option.name}
                  getOptionValue={option => option.value}
                  styles={{
                    control: (base, state) => ({
                      ...base,
                      borderColor: missingFields.includes('billing_parameter_name') && !newRecord.billing_parameter_name ? '#FF3B30' : base.borderColor,
                      '&:hover': {
                        borderColor: missingFields.includes('billing_parameter_name') && !newRecord.billing_parameter_name ? '#FF3B30' : base.borderColor,
                      }
                    })
                  }}
                />
              </Td>
              <Td>
                <Input
                  type="text"
                  value={newRecord.trip_count}
                  onChange={(e) => onNewRecordChange('trip_count', e.target.value)}
                  placeholder="Enter trip count"
                  hasError={missingFields.includes('trip_count') && !newRecord.trip_count}
                />
              </Td>
              <Td>
                <Input
                  type="text"
                  value={newRecord.freight_rate}
                  onChange={(e) => onNewRecordChange('freight_rate', e.target.value)}
                  placeholder="Enter freight rate"
                  hasError={missingFields.includes('freight_rate') && !newRecord.freight_rate}
                />
              </Td>
              <Td>
                <DateRangeWrapper style={
                  ((missingFields.includes('contract_tenure_from') && !newRecord.contract_tenure_from) || (missingFields.includes('contract_tenure_to') && !newRecord.contract_tenure_to))
                    ? { border: '1px solid #FF3B30', borderRadius: 4, background: '#FFF0F0', padding: 2 }
                    : {}
                }>
                  <DateRangePicker
                    startDate={newRecord.contract_tenure_from && Moment(newRecord.contract_tenure_from)}
                    endDate={newRecord.contract_tenure_to && Moment(newRecord.contract_tenure_to)}
                    onDatesChange={onDateChange}
                    focusedInput={focusedInput}
                    onFocusChange={focusedInput => setFocusedInput(focusedInput)}
                    startDatePlaceholderText="Start"
                    endDatePlaceholderText="End"
                    displayFormat="DD/MM/YYYY"
                    numberOfMonths={2}
                    showClearDates
                    small
                    withPortal
                    appendToBody
                    customArrowIcon={<span>→</span>}
                  />
                </DateRangeWrapper>
              </Td>
              <Td>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <SaveButton onClick={onSaveNewRecord}>Save</SaveButton>
                  <CancelIcon fill={'#FF3B30'} onClick={onCancelAdd} width={20} height={20} style={{ cursor: 'pointer' }} />
                </div>
              </Td>
            </tr>
          )}
          {data.map((record, index) => (
            <tr key={record.transport_management_id}>
              <Td>{((currentPage-1)*10)+(index + 1)}</Td>
              <Td>{renderCell(record, 'provider_name')}</Td>
              <Td>{renderCell(record, 'vehicle_type')}</Td>
              <Td>{renderCell(record, 'contract_type_name')}</Td>
              <Td>{renderCell(record, 'route_code')}</Td>
              <Td>{renderCell(record, 'vehicle_reg_no')}</Td>
              <Td>{renderCell(record, 'fixed_cost')}</Td>
              <Td>{renderCell(record, 'excluded_route_code')}</Td>
              <Td>{renderCell(record, 'included_route_code')}</Td>
              <Td>{renderCell(record, 'billing_parameter_name')}</Td>
              <Td>{renderCell(record, 'trip_count')}</Td>
              <Td>{renderCell(record, 'freight_rate')}</Td>
              <Td>{renderCell(record, 'contract')}</Td>
              <Td>
                {editingId === record.transport_management_id ? (
                  renderCell(record, 'actions')
                ) : (
                  <div style={{ display: 'flex', justifyContent: 'center' , alignItems: 'center', gap: '8px'}}>
                    <ActionButton onClick={() => handleEdit(record)}>
                      <EditIcon fill={'#627fe9'} onClick={() => handleEdit(record)}/>
                    </ActionButton>
                    <ActionButton onClick={() => onDelete(record.transport_management_id)}>
                      <DeleteIcon />
                    </ActionButton>
                  </div>
                )}
              </Td>
            </tr>
          ))}
        </tbody>
      </Table>
    </TableWrapper>
  );
};

export default DataTable; 